import { useCallback } from 'react';

import { JobCardPdfRecipientType } from '~hooks/useJobCardPdf/models';
import useBulkJobCardPdfs from '~hooks/useJobCardPdf/useBulkJobCardPdfs';
import useLoadDigestPdf from '~hooks/useLoadDigestPdf/useLoadDigestPdf';
import { LoadDigestLoadFilter } from '~hooks/useLoadDigests/models';
import { Order } from '~hooks/useOrders/models';
import { useStores } from '~store';

interface OrderMenuHandlersOptions {
  onDownloadFiles?: () => void;
  onEmailLoadDigest?: () => void;
  order: Order;
}

export default function useOrderMenuHandlers({
  onDownloadFiles,
  onEmailLoadDigest,
  order,
}: OrderMenuHandlersOptions) {
  const { ordersDispatchStore } = useStores();
  const { generateJobCardPdfsForOrder } = useBulkJobCardPdfs();
  const { viewForOrder } = useLoadDigestPdf();

  const hasLoadedJobs = (ordersDispatchStore.orderJobs.get(order.id)?.length ?? 0) > 0;
  const orderHasJobs = (order.orderSummary?.jobsCount ?? 0) > 0 || hasLoadedJobs;
  const orderHasLoads = (order.orderSummary?.loadsCount ?? 0) > 0;

  const downloadJobCardPdfs = useCallback(
    (recipientType: JobCardPdfRecipientType) => {
      generateJobCardPdfsForOrder(order.id, recipientType);
    },
    [generateJobCardPdfsForOrder, order.id],
  );

  const viewLoadDigest = useCallback(
    (loadFilter: LoadDigestLoadFilter) => {
      void viewForOrder(order.id, loadFilter);
    },
    [order.id, viewForOrder],
  );

  return {
    onDownloadFiles: hasLoadedJobs ? onDownloadFiles : undefined,
    onDownloadJobCardPdfs: orderHasJobs ? downloadJobCardPdfs : undefined,
    onEmailLoadDigest: orderHasLoads ? onEmailLoadDigest : undefined,
    onViewLoadDigest: orderHasLoads ? viewLoadDigest : undefined,
  };
}
